import {useEffect, useState} from "react";
import {Modal} from "antd";
import {DeleteOutlined} from "@ant-design/icons";
import {FormSubmitHandlerProps, OfficeItem} from "../types/office";
import {useForm} from "antd/es/form/Form";
import {useParams} from "react-router-dom";
import {officeService} from "../services/officeService";
import {Query} from "appwrite";
import {openSuccessNotification, showErrorNotification} from "../helpers/mainHelpers";

export const useOfficeController = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [officesList, setOfficesList] = useState<OfficeItem[]>([])
  const [selectedOffice, setSelectedOffice] = useState<OfficeItem | undefined>(undefined)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [lastUpdate, setLastUpdate] = useState(0)
  const [officeForm] = useForm()
  const {departmentId} = useParams()


  useEffect(() => {
    if (!isCreateModalOpen) {
      setSelectedOffice(undefined)
    }
  }, [isCreateModalOpen])

  const toggleCreateModalVisible = () => {
    setIsCreateModalOpen(!isCreateModalOpen)
  }

  const toggleLastUpdate = () => {
    setLastUpdate(Date.now)
  }

  useEffect(() => {
    let queries: string[] = []
    if (departmentId) {
      queries = [...queries, Query.equal('department', departmentId)]
    }
    fetchOfficesList(queries)
  }, [lastUpdate])


  const getOfficeItem = async (id: string) => {
    setIsLoading(true)
    try {
      const currentOffice = await officeService.getOfficeItem(id)
      setSelectedOffice(currentOffice)
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка завантаження"});
    } finally {
      setIsLoading(false)
    }
  }

  const fetchOfficesList = async (queries: string[]) => {
    setIsLoading(true)
    try {
      const list = await officeService.list(queries)
      setOfficesList(list)
    } catch (error) {
      console.error(error)
      showErrorNotification({message: "Помилка завантаження"});
    } finally {
      setIsLoading(false)
    }
  }


  const confirmDeleteOffice = (props: { officeId: string, officeName: string }) => {
    const {officeName, officeId} = props
    const {confirm} = Modal;

    confirm(
      {
        width: 400,
        icon: <DeleteOutlined/>,
        title: `Видалити ${officeName} ?`,
        okText: "Видалити",
        okButtonProps: {danger: true},
        cancelText: "Cкасувати",
        onOk: async () => {
          deleteOfficeHandler(officeId)
        },
      });

  }

  const deleteOfficeHandler = async (id: string) => {
    setIsLoading(true)
    try {
      await officeService.delete(id)
      toggleLastUpdate()
      openSuccessNotification("Кабінет успішно видалено!")
    } catch (error) {
      showErrorNotification({message: "Помилка видалення кабінету", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const createOfficeHandler = async (data: OfficeItem) => {
    setIsLoading(true)
    try {
      const createdOffice = await officeService.create(data)
      openSuccessNotification("Кабінет успішно створено!")
      toggleCreateModalVisible()
      toggleLastUpdate()
    } catch (error) {
      showErrorNotification({message: "Помилка створення кабінету", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const updateOfficeHandler = async (data: OfficeItem) => {
    setIsLoading(true)
    try {
      const updatedOffice = await officeService.update(data)
      toggleCreateModalVisible()
      toggleLastUpdate()
      openSuccessNotification("Кабінет успішно оновлено!")
    } catch (error) {
      showErrorNotification({message: "Помилка оновлення кабінету", description: JSON.stringify(error)});
    } finally {
      setIsLoading(false)
    }
  }

  const formSubmitHandler = async (data: FormSubmitHandlerProps) => {
    const fullOfficeData = {
      ...data,
      department: departmentId || "",
    }
    if (selectedOffice) {
      // update
      await updateOfficeHandler({...fullOfficeData, $id: selectedOffice?.$id || ""})
    } else {
      // create
      await createOfficeHandler(fullOfficeData)
    }

  }

  const editButtonHandler = (selectedOffice: OfficeItem) => {
    setSelectedOffice(selectedOffice)
    toggleCreateModalVisible()
  }


  return {
    confirmDeleteOffice,
    toggleCreateModalVisible,
    isLoading,
    officesList,
    officeForm,
    isCreateModalOpen,
    formSubmitHandler,
    fetchOfficesList,
    getOfficeItem,
    editButtonHandler,
    selectedOffice,
  }

}